/**
 * The "show more trains" count, kept across reloads for the view it was asked in.
 *
 * Stored beside the journey's own progress, under the same per-day rule, but keyed by the card
 * question too: reaching the platform is a different view than standing at school, and this
 * count must not carry between them, nor into tomorrow. The format itself lives in core, where
 * it can be tested.
 */

import { useCallback, useEffect, useState } from "react";
import { parseTrainWindow, serialiseTrainWindow } from "../core/train-window";

function storageKey(journeyId: string): string {
	return `gotime.moreTrains.${journeyId}`;
}

/** A count belongs to the day it was asked on; yesterday's asking is not this morning's. */
function today(): string {
	return new Date().toLocaleDateString("en-CA");
}

function read(journeyId: string, view: string): number {
	try {
		return parseTrainWindow(localStorage.getItem(storageKey(journeyId)), today(), view);
	} catch {
		return 0;
	}
}

export function useExtraTrains(journeyId: string, view: string): [number, () => void] {
	const [extra, setExtra] = useState(() => read(journeyId, view));
	// Swapped during render rather than in an effect, so a new view's count is never read through
	// the previous view's stale state first.
	const [loadedKey, setLoadedKey] = useState(`${journeyId}:${view}`);
	const key = `${journeyId}:${view}`;
	if (loadedKey !== key) {
		setLoadedKey(key);
		setExtra(read(journeyId, view));
	}

	useEffect(() => {
		try {
			localStorage.setItem(storageKey(journeyId), serialiseTrainWindow(view, extra, today()));
		} catch {
			// Private browsing; the count simply will not survive a reload.
		}
	}, [journeyId, view, extra]);

	const requestMore = useCallback(() => setExtra((current) => current + 1), []);

	return [extra, requestMore];
}
